import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, Building2, Activity, Shield, Loader2, TrendingUp, Globe, Zap, Euro, ArrowRight, ShieldAlert, CreditCard } from 'lucide-react';
import { adminStats, adminListSubscriptions } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav('/login'); return; }
    if (!user.is_admin) { setError('Accesso riservato agli amministratori'); return; }
    adminStats().then(setStats).catch(() => setError('Errore caricamento stats'));
    adminListSubscriptions().then(setSubs).catch(() => {});
  }, [user, authLoading, nav]);

  if (error) return (
    <div className="min-h-[70vh] flex items-center justify-center py-16">
      <Card className="max-w-md w-full mx-4 p-8 text-center border border-slate-200">
        <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-600 border border-red-100"><ShieldAlert className="h-7 w-7" /></div>
        <h1 className="mt-4 text-2xl font-extrabold text-slate-900">Accesso negato</h1>
        <p className="mt-2 text-slate-600 text-sm">{error}</p>
        <Button asChild className="mt-6 bg-[#0d63b0] hover:bg-[#0a4f8f]"><Link to="/">Torna alla home</Link></Button>
      </Card>
    </div>
  );

  if (!stats) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0d63b0]" /></div>;

  return (
    <div className="py-10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-amber-700 flex items-center gap-1.5"><Shield className="h-3.5 w-3.5" /> Admin</div>
            <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-slate-900">Pannello amministrazione</h1>
            <p className="mt-1 text-slate-600 text-sm">Utenti, brand, abbonamenti e statistiche piattaforma.</p>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          <StatCard icon={Users} label="Utenti registrati" value={stats.total_users} sub={`+${stats.recent_users_7d} negli ultimi 7g`} color="#0d63b0" />
          <StatCard icon={Building2} label="Brand attivi" value={stats.total_brands} sub={`${stats.verified_domains} con dominio verificato`} color="#10b981" />
          <StatCard icon={Activity} label="Push totali" value={stats.total_pushes} sub={`${stats.active_pushes} attivi`} color="#f59e0b" />
          <StatCard icon={Euro} label="MRR (mock)" value={`€${stats.revenue_monthly_eur}`} sub="Ricavo mensile ricorrente" color="#6366f1" />
        </div>

        {/* Plan distribution */}
        <Card className="border border-slate-200 p-6 mb-6">
          <div className="font-semibold text-slate-900 mb-4 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#0d63b0]" /> Distribuzione piani</div>
          <div className="grid grid-cols-3 gap-4">
            {Object.entries(stats.plan_counts).map(([plan, count]) => (
              <div key={plan} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{plan}</div>
                <div className="mt-1 text-3xl font-extrabold text-slate-900">{count}</div>
                <div className="text-xs text-slate-500 mt-1">brand su piano {plan}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Quick actions */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <QuickCard to="/admin/users" icon={Users} title="Gestione utenti" desc="Cerca, filtra e monitora gli utenti registrati" />
          <QuickCard to="/admin/brands" icon={Building2} title="Gestione brand" desc="Elenco brand con piano, dominio e attività" />
          <QuickCard to="/admin/settings" icon={CreditCard} title="Pagamenti & Email" desc="PayPal, SMTP, Microsoft 365" />
        </div>

        {/* Subscriptions */}
        <Card className="border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-semibold text-slate-900 flex items-center gap-2"><CreditCard className="h-4 w-4 text-[#0d63b0]" /> Abbonamenti attivi</div>
            <span className="text-xs text-slate-500">{subs?.length ?? 0} attivi</span>
          </div>
          {subs === null && <div className="text-slate-500 text-sm">Caricamento...</div>}
          {subs && subs.length === 0 && (
            <div className="text-center py-8 text-slate-500 text-sm">Nessun abbonamento attivo al momento.</div>
          )}
          {subs && subs.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200">
                    <th className="py-2">Brand</th>
                    <th>Owner</th>
                    <th>Piano</th>
                    <th className="text-right">Prezzo</th>
                    <th>Attivato</th>
                    <th>Provider</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map((s) => (
                    <tr key={s.brand_slug} className="border-b border-slate-100">
                      <td className="py-3">
                        <div className="font-semibold text-slate-900">{s.brand_name}</div>
                        <div className="text-[11px] font-mono text-slate-500">/b/{s.brand_slug}</div>
                      </td>
                      <td className="text-slate-700">{s.owner_email}</td>
                      <td><span className="inline-flex rounded-full bg-blue-50 text-[#0d63b0] border border-blue-100 px-2 py-0.5 text-xs font-semibold capitalize">{s.plan}</span></td>
                      <td className="text-right font-mono font-semibold">€{s.price_eur}</td>
                      <td className="text-slate-500 text-xs">{s.updated_at ? new Date(s.updated_at).toLocaleString('it-IT') : '—'}</td>
                      <td><span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${s.mock_payment ? 'bg-amber-50 text-amber-700 border border-amber-100' : 'bg-emerald-50 text-emerald-700 border border-emerald-100'}`}>{s.payment_provider}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <Card className="border border-slate-200 p-5">
      <div className="flex items-center gap-2 text-xs text-slate-500 font-semibold uppercase tracking-widest">
        <Icon className="h-3.5 w-3.5" style={{ color }} /> {label}
      </div>
      <div className="mt-2 text-3xl font-extrabold text-slate-900">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{sub}</div>
    </Card>
  );
}

function QuickCard({ to, icon: Icon, title, desc }) {
  return (
    <Link to={to}>
      <Card className="border border-slate-200 p-5 hover:border-[#0d63b0]/60 hover:shadow-md transition-all">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center"><Icon className="h-5 w-5 text-[#0d63b0]" /></div>
          <div className="flex-1">
            <div className="font-semibold text-slate-900">{title}</div>
            <div className="text-xs text-slate-500">{desc}</div>
          </div>
          <ArrowRight className="h-4 w-4 text-slate-400" />
        </div>
      </Card>
    </Link>
  );
}
